<template>
  <div
    class="index"
    role="status"
  >
    <notification-panel
      v-if="displayNotification"
      v-bind="getActiveNotification"
      toast
      @close="handleNotificationPanelClose"
    />
  </div>
</template>

<script>

/**
 * Vue: Index (core)
 * -----------------------------------------------------------------------------
 * Used to set index states and global event listeners.
 * - Also updates component loading states when overlays become active.
 * - Displays notifications, container acts as a status live region, example 4p
 *   in these tests https://codepen.io/scottohara/pen/dyzxwyr
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import breakpoints from '~/config/breakpoints'
import { debounce, isCdnUrl, throttle } from '~/helpers/general'

import config from '~/../../canvas.config'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Index',

  components: {
    NotificationPanel: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.notification-panel' */
        '~async/utils/notification-panel/notification-panel'
      ),
    }),
  },

  data() {
    return {
      cnvsGlobal: true,
      screen: {
        breakpoint: 'xs',
        mobile: true,
        height: 0,
        width: 0,
      },
      scroll: {
        direction: 'none',
        header: 'open',
        top: true,
        x: 0,
        y: 0,
      },
      isPDP: document.querySelector('.template-product'),
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveNotification: 'getActiveNotification',
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeOverlays: (state) => state.overlays.active,
      pausedScroll: (state) => state.index.scroll.paused,
      settings: (state) => state.index.settings,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if notification should be displayed.
     * @returns {Boolean}
     */
    displayNotification() {
      const notification = this.getActiveNotification

      if (!notification.displayUrl) {
        return notification
      }

      /**
       * If the notification display URL matches the previous URL then user has
       * navigated away before notification could close.
       * - Close notification to avoid showing it again.
       * - Can't use previous URL state as it hasn't been updated yet.
       */
      if (notification.displayUrl === sessionStorage.getItem('previousUrl')) {
        this.handleNotificationPanelClose()
        return false
      }

      return notification.displayUrl === location.pathname
    },
  },

  watch: {

    /**
     * Watch active overlay to update component loading state.
     */
    getActiveOverlay() {
      const overlay = this.getActiveOverlay

      /**
       * Add class to hide attentive widgets when overlays are open (except for
       * One Trust). This is due to the inability to differentiate between the
       * bottom left overlay vs the full screen takeover.
       */
      const isOneTrust = overlay?.namespace.toLowerCase().includes('onetrust')
      document.body.classList[
        overlay && !isOneTrust
          ? 'add'
          : 'remove'
      ]('has-overlay')

      if (!overlay?.component) {
        return
      }

      const components = Array.isArray(overlay.component)
        ? overlay.component
        : [overlay.component]

      components.forEach((handle) => {
        this.loadComponent(handle)
      })
    },

    /**
     * Watch active notification to load notification panel.
     */
    getActiveNotification() {
      this.loadNotificationPanel()
    },
  },

  mounted() {
    this.rehydrateSite()
    this.setMobileBreakpoints(config.mobileBreakpoints)
    this.getPreviousUrl()

    this.handleResize()
    this.handleScroll(true)

    this.setEventListeners()
    this.setEventBusListeners()

    this.loadNotificationPanel()
    this.setTrackingHistory()

    if (this.isIos()) {
      document.body.classList.add('ios-device')
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      closeAllOverlays: 'overlays/closeAll',
      loadComponent: 'components/load',
      loadStylesheet: 'components/loadStylesheet',
      openOverlay: 'overlays/open',
      rehydrateSite: 'rehydrateSite',
      setCartResponse: 'cart/setResponse',
      setMobileBreakpoints: 'setMobileBreakpoints',
      setNotification: 'setNotification',
      setPreviousUrl: 'setPreviousUrl',
      setScreen: 'setScreen',
      setScroll: 'setScroll',
      setStylesheet: 'setStylesheet',
    }),

    /**
     * Get previous URL from sessionStorage.
     * - State doesn't update in time if set in the `beforeunload` event.
     */
    getPreviousUrl() {
      const previousUrl = sessionStorage.getItem('previousUrl')

      if (previousUrl) {
        this.setPreviousUrl(previousUrl)
      }
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.setStylesheetListeners()

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.handleResize(), this.settings.throttle),
        { passive: true },
      )

      const scrollElement = this.isPDP && this.isMobile ? document.getElementById('vue-element') : window
      scrollElement.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(), this.settings.throttle),
        { passive: true },
      )

      /**
       * Set previous URL just before changing page.
       */
      window.addEventListener('pagehide', () => {
        cnvs.SessionStorage('previousUrl', location.pathname)
      })

      /**
       * Fade out page when navigating to new page.
       * - Closes all overlays as well.
       */
      if (!this.$variable('settings.globalFadeOut')) {
        return
      }

      window.addEventListener('click', (event) => {
        const anchor = event.target?.matches('a[href]')
          ? event.target
          : event.target?.closest('a[href]')

        if (!anchor || anchor?.dataset?.button === 'true') {
          return
        }

        const url = anchor.href

        // If anchor change only, do nothing
        if (url?.split('#')[0] === location.href?.split('#')[0] || url.startsWith('tel:')) {
          return
        }

        event.preventDefault()

        document.body.classList.add(this.$class('updating'))
        this.closeAllOverlays()

        setTimeout(() => {
          location.href = url
        }, this.$timing('quick'))
      })
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {

      /**
       * Overlay listeners act as fallbacks for non-Vue components.
       * - Also used by cnvs.Helpers.openOverlay() CLI.
       */
      cnvs.EventBus.listen('overlay:open', (overlay) => {
        this.openOverlay(overlay)

        if (overlay.component || !overlay.stylesheet) {
          return
        }

        this.loadStylesheet(overlay.stylesheet)
      })

      cnvs.EventBus.listen('overlay:close', (overlay) => {
        this.closeOverlay(overlay)
      })

      /**
       * Cart update fallback listener.
       */
      cnvs.EventBus.listen('cart:update', async() => {
        await this.setCartResponse()
        window.cnvs?.EventBus?.emit('overlay:open-cart-drawer')
      })

      /**
       * Set notification fallback listener.
       * - Used for logout notification.
       */
      cnvs.EventBus.listen('cnvs:set-notification', (notification) => {
        this.setNotification(notification)
      })
    },

    /**
     * Listen for stylesheets loading to trigger events after they've loaded.
     */
    setStylesheetListeners() {
      const stylesheets = [...document.querySelectorAll('[rel="stylesheet"]')]

      stylesheets.forEach((element) => {
        const media = element.getAttribute('media')
        let stylesheet = element.getAttribute('href')

        if (isCdnUrl(stylesheet)) {
          stylesheet = stylesheet
            .split('/')
            .reverse()[0]
            .split('?')[0]
        }

        if (!stylesheet) {
          return
        }

        /**
         * If it's already loaded then add to state.
         */
        if (media === 'all') {
          this.setStylesheet(stylesheet)
          return
        }

        /**
         * Otherwise listen for load.
         */
        element.addEventListener('load', () => {
          element.setAttribute('media', 'all')
          this.setStylesheet(stylesheet)
        })
      })
    },

    /**
     * Handle resize.
     */
    handleResize() {
      this.screen = {
        height: window.visualViewport.height,
        width: window.innerWidth,
      }

      /**
       * Set CSS variable.
       */
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${this.screen.height}px`,
      )

      for (const [breakpoint, value] of Object.entries(breakpoints)) {
        if (!window.matchMedia(`(min-width: ${value})`).matches) {
          break
        }

        this.screen.breakpoint = breakpoint
      }

      this.screen.mobile = this.settings.mobileBreakpoints.includes(this.screen.breakpoint)

      cnvs.EventBus.emit('cnvs:resize', this.screen)
      this.setScreen(this.screen)
    },

    /**
     * Handle scroll.
     * - Don't update state when overlay opens.
     * @param {Boolean} [start] - If setting on initial load.
     */
    handleScroll(start = false) {
      const excludedOverlays = ['quick-add-to-cart-modal']
      const vueElement = document.getElementById('vue-element')
      const scrollY = this.isPDP && this.isMobile ? vueElement.scrollTop : window.scrollY
      const scrollX = this.isPDP && this.isMobile ? vueElement.scrollLeft : window.scrollX

      const filteredOverlays = this.activeOverlays?.filter((overlay) => {
        if (overlay.namespace === 'quick-add-success-modal' || overlay.namespace === 'oneTrustModal' || overlay.namespace === 'multiStoreModal') {
          return false
        }
        return !excludedOverlays.includes(overlay.namespace)
      })

      if (filteredOverlays.length) {
        return
      }

      /**
       * Set object on start.
       */
      if (start) {
        this.scroll = {
          direction: 'none',
          header: scrollY > this.settings.start ? 'minimised' : 'open',
          productBreadcrumbsMobile: 'minimised',
          top: scrollY <= this.settings.start,
          x: scrollX,
          y: scrollY,
        }

        this.setScroll(this.scroll)
        return
      }

      /**
       * If scroll is paused or if scroll difference is less than threshold
       * don't update scroll.
       */
      const difference = scrollY - this.scroll.y

      if (
        this.pausedScroll ||
        Math.abs(difference) < this.settings.threshold
      ) {
        return
      }

      let headerClass = `header--${this.scroll.header}`
      document.documentElement.classList.remove(headerClass)

      this.scroll = {
        direction: difference > 0 ? 'down' : 'up',
        top: scrollY <= this.settings.start,
        productBreadcrumbsMobile: this.scroll.productBreadcrumbsMobile,
        x: scrollX,
        y: scrollY,
      }

      if (this.scroll.direction === 'up' && !this.scroll.top) {
        this.scroll.header = 'minimised'
      } else if (scrollY > this.settings.start) {
        this.scroll.header = 'collapsed'
      } else {
        this.scroll.header = 'open'
        this.scroll.productBreadcrumbsMobile = 'open'
      }

      cnvs.EventBus.emit('cnvs:scroll', this.scroll)
      this.setScroll(this.scroll)

      headerClass = `header--${this.scroll.header}`
      document.documentElement.classList.add(headerClass)
    },

    /**
     * Loads notification panel on mounted or when active notification changes.
     */
    loadNotificationPanel() {
      if (
        !this.getActiveNotification ||
        this.getComponentActive('notification-panel')
      ) {
        return
      }

      this.loadComponent('notification-panel')
    },

    /**
     * Handle notification panel close.
     */
    handleNotificationPanelClose() {
      this.setNotification(false)
    },

    /**
     * Resets tra
     */
    setTrackingHistory() {
      if (!this.isPDP) {
        cnvs.LocalStorage('trackingHistory', JSON.stringify({}))
      }
    },

    /**
     * Checks for iOS devices
     */
    isIos() {
      const userAgent = window.navigator.userAgent
      const ios = (/iPad|iPhone|iPod/).test(userAgent)
      return ios
    },
  },
}
</script>
